import { getAutoSuggest } from '@api/autosuggest';
import { TData } from '@components/types';

// Autosuggest city/state rows also carry the place's coordinates under `meta`.
type TPlace = TData & { meta?: { latitude?: number; longitude?: number } };
import { geoErrorMessage, getLocation, isValidLocation } from '@core/geolocation';
import { message, notification } from 'antd';
import Cookies from 'js-cookie';
import { Loader, LocateFixed, MapPin, Search, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';

const POPULAR_CITIES = ['Goa', 'Dehradun', 'Jaipur', 'Rishikesh', 'Shimla', 'Delhi', 'Mumbai', 'Bengaluru'];

// 'Rishikesh, Uttarakhand' -> title 'Rishikesh', subtitle 'Uttarakhand'
const splitPlace = (name?: string) => {
  const [title, ...rest] = (name || '').split(',');
  return { title: title.trim(), subtitle: rest.join(',').trim() };
};

interface Props {
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  isNotClosable?: boolean;
  setIsNotClosable?: Function;
  handleCallback?: Function;
}

const Location: React.FC<Props> = ({ showModal, setShowModal, isNotClosable = false, setIsNotClosable, handleCallback }) => {
  const [searchResult, setSearchResult] = useState<TPlace[]>([]);
  const [term, setTerm] = useState('');
  const [searching, setSearching] = useState(false);
  const [active, setActive] = useState(-1);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const runIdRef = useRef(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentLocation, setCurrentLocation] = useState<string | null>(null);

  const [api, contextHolder] = notification.useNotification();

  // ── Load saved location name on open ──────────────────────────────────────
  useEffect(() => {
    if (showModal) {
      try {
        const saved = JSON.parse(localStorage.getItem('location') ?? 'null');
        setCurrentLocation(saved?.Location?.name ?? null);
      } catch {
        setCurrentLocation(null);
      }
    }
  }, [showModal]);

  const onClose = () => {
    if (isNotClosable) {
      message.error('Location access is required here.');
    } else {
      setShowModal(false);
    }
  };

  // ── Clear saved location ───────────────────────────────────────────────────
  const resetLocation = () => {
    localStorage.removeItem('location');
    localStorage.removeItem('geoposition');
    Cookies.remove('location');
    setCurrentLocation(null);
    api.success({ message: 'Location cleared', placement: 'bottomRight' });
    handleCallback && handleCallback(null);
  };

  const fetchUserLocation = () => {
    if (!navigator.geolocation) {
      api.error({
        message: "Geolocation isn't supported by your browser",
        placement: 'bottomRight',
      });
      return;
    }
    setIsLoading(true);
    getLocation(true)
      ?.then((location) => {
        if (isValidLocation(location)) {
          api.success({ message: 'Location saved', placement: 'bottomRight' });
          setIsNotClosable?.(false);
          setShowModal(false);
        } else {
          api.error({
            message: geoErrorMessage(location?.code),
            placement: 'bottomRight',
          });
        }
        setIsLoading(false);
      })
      .catch(() => {
        api.error({ message: 'Oops! something went wrong.', placement: 'bottomRight' });
        setIsLoading(false);
      });
  };

  const runSearch = (value: string) => {
    const q = value.trim();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setActive(-1);
    if (!q) {
      runIdRef.current++;
      setSearchResult([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    debounceRef.current = setTimeout(() => {
      const runId = ++runIdRef.current;
      getAutoSuggest(q, 'citystate')
        .then((res) => { if (runId === runIdRef.current) setSearchResult(Array.isArray(res) ? (res as TPlace[]) : []); })
        .catch(() => { if (runId === runIdRef.current) setSearchResult([]); })
        .finally(() => { if (runId === runIdRef.current) setSearching(false); });
    }, 250);
  };

  const onTermChange = (value: string) => {
    setTerm(value);
    runSearch(value);
  };

  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  // Fresh search state each time the modal opens.
  useEffect(() => {
    if (showModal) {
      setTerm('');
      setSearchResult([]);
      setActive(-1);
    }
  }, [showModal]);

  const selectPlace = (item: TPlace) =>
    addLocation(JSON.stringify({
      Location: item,
      Coords: {
        latitude: item.meta?.latitude || 27.504,
        longitude: item.meta?.longitude || 77.669,
      },
    }));

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => (searchResult.length ? (i + 1) % searchResult.length : -1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (searchResult.length ? (i <= 0 ? searchResult.length - 1 : i - 1) : -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const pick = searchResult[active >= 0 ? active : 0];
      if (pick) selectPlace(pick);
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  const addLocation = (value: string) => {
    if (!value) return;
    try {
      const parsed = JSON.parse(value);
      localStorage.setItem('location', value);
      Cookies.set('location', parsed?.Coords);
      localStorage.setItem(
        'geoposition',
        JSON.stringify({ ...parsed.Coords, last_updated: Date.now() })
      );
      setCurrentLocation(parsed?.Location?.name ?? null);
      api.success({ message: 'Location updated', placement: 'bottomRight' });
      setShowModal(false);
      handleCallback && handleCallback(parsed?.Location?.id);
    } catch {
      api.error({ message: 'Failed to save location', placement: 'bottomRight' });
    }
  };

  if (!showModal) return null;

  return (
    <div>
      {contextHolder}

      {/* ── Backdrop ── */}

      <div className="opacity-25 fixed inset-0 z-40 bg-black" onClick={onClose} />

      {/* ── Modal ── */}
      <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none p-4">
        <div
          className="w-full md:max-w-[500px] relative"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="border-0 rounded-2xl shadow-xl flex flex-col w-full bg-white outline-none">

            {/* ── Header ── */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-100">
              <div className="flex items-center gap-2">
                <MapPin size={18} className="text-orange-500" />
                <h3 className="text-base font-semibold text-neutral-800">
                  Set your location
                </h3>
              </div>
              <button
                onClick={onClose}
                className="p-1 rounded-lg hover:bg-neutral-100 transition-colors border-none bg-transparent cursor-pointer"
              >
                <X size={18} className="text-neutral-500" />
              </button>
            </div>

            {/* ── Body ── */}
            <div className="flex flex-col gap-4 p-5">

              {/* Current location pill */}
              {currentLocation && (
                <div className="flex items-center justify-between gap-2 px-3 py-2 bg-orange-50 rounded-xl border border-orange-100">
                  <div className="flex items-center gap-2 min-w-0">
                    <MapPin size={14} className="text-orange-500 flex-shrink-0" />
                    <span className="font-manrope text-sm font-semibold text-orange-700 truncate">
                      {currentLocation}
                    </span>
                  </div>
                  <button
                    onClick={resetLocation}
                    className="font-manrope flex items-center gap-1 text-xs font-semibold text-red-500 hover:text-red-700 transition-colors border-none bg-transparent cursor-pointer whitespace-nowrap flex-shrink-0"
                  >
                    <X size={12} />
                    Reset
                  </button>
                </div>
              )}

              {/* Search */}
              <div className="flex flex-col gap-2">
                <label htmlFor="location-search" className="text-xs font-semibold text-neutral-500 uppercase tracking-wider">
                  Search city or area
                </label>
                <div className="flex items-center gap-2 bg-neutral-50 border border-solid border-neutral-200 rounded-xl px-3 focus-within:border-neutral-400 focus-within:bg-white transition-colors">
                  <Search size={16} className="text-neutral-400 flex-shrink-0" aria-hidden="true" />
                  <input
                    id="location-search"
                    ref={inputRef}
                    autoFocus
                    type="text"
                    value={term}
                    onChange={(e) => onTermChange(e.target.value)}
                    onKeyDown={onKeyDown}
                    placeholder="e.g. Rishikesh, Goa, Jaipur…"
                    role="combobox"
                    aria-expanded={searchResult.length > 0}
                    aria-controls="location-options"
                    aria-autocomplete="list"
                    aria-activedescendant={active >= 0 ? `location-option-${active}` : undefined}
                    autoComplete="off"
                    className="flex-1 min-w-0 h-11 bg-transparent border-0 outline-none text-sm text-neutral-800 placeholder:text-neutral-400"
                  />
                  {searching && <Loader size={14} className="animate-spin text-neutral-400 flex-shrink-0" />}
                  {term && !searching && (
                    <button type="button" aria-label="Clear search" onClick={() => onTermChange('')}
                      className="p-1 rounded-full text-neutral-400 hover:bg-neutral-100 border-0 bg-transparent cursor-pointer">
                      <X size={14} />
                    </button>
                  )}
                </div>

                {term.trim() ? (
                  <ul id="location-options" role="listbox" className="list-none p-1 m-0 max-h-60 overflow-auto rounded-xl border border-solid border-neutral-100 bg-white">
                    {!searching && searchResult.length === 0 && (
                      <li className="px-3 py-3 text-sm text-neutral-500">No results — try a different city</li>
                    )}
                    {searchResult.map((item, i) => {
                      const { title, subtitle } = splitPlace(item.name);
                      return (
                        <li
                          key={item.id ?? item.name}
                          id={`location-option-${i}`}
                          role="option"
                          aria-selected={i === active}
                          onMouseEnter={() => setActive(i)}
                          onMouseDown={(e) => { e.preventDefault(); selectPlace(item); }}
                          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer ${i === active ? 'bg-neutral-100' : ''}`}
                        >
                          <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-neutral-100 text-neutral-500 flex-shrink-0">
                            <MapPin size={16} />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm font-semibold text-neutral-800 truncate">{title}</span>
                            {subtitle && <span className="block text-xs text-neutral-400 truncate">{subtitle}</span>}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <div className="flex flex-col gap-2">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-neutral-400">Popular cities</span>
                    <div className="flex flex-wrap gap-2">
                      {POPULAR_CITIES.map((city) => (
                        <button
                          key={city}
                          type="button"
                          onClick={() => { onTermChange(city); inputRef.current?.focus(); }}
                          className="px-3 py-1.5 rounded-full border border-solid border-neutral-200 bg-white text-xs font-semibold text-neutral-600 hover:border-neutral-400 hover:bg-neutral-50 transition-colors cursor-pointer"
                        >
                          {city}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Divider */}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-neutral-100" />
                <span className="text-xs text-neutral-400 font-medium">or</span>
                <div className="flex-1 h-px bg-neutral-100" />
              </div>

              {/* GPS button */}
              <button
                type="button"
                onClick={fetchUserLocation}
                disabled={isLoading}
                className="w-full h-11 flex justify-center items-center gap-2 rounded-xl border-0 text-white hover:opacity-90 transition-opacity cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed font-semibold text-sm"
                style={{ backgroundColor: '#ED7829' }}
              >
                {isLoading ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Detecting location…
                  </>
                ) : (
                  <>
                    <LocateFixed className="w-4 h-4" />
                    Use my current location
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Location;